// Forms.jsx
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Form } from 'react-final-form';
import { Nav } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { BorderedBottomTabs, CustomNavLink, NavItem } from '@spark/ui-components/components/Tabs';
import { notification } from '@spark/ui-components/utils/notification';
import { formStructure, formTabs, validateForm } from './formConfig';
import { getCandidatoById, createBeneficio, updateBeneficio, statusVisualizado } from '@spark/services/recrutamento-rh';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import CandidatoQuickActions from '../../components/CandidatoQuickActions';
import ExperienciasProfissionais from './ExperienciasProfissionais';
import EnderecoMap from './EnderecoMap';

const Forms = ({ isReadOnly = false }) => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [initialData, setInitialData] = useState(null);

  const isEditMode = !!id && id !== 'criar' && !isReadOnly;

  const mapApiToForm = (d = {}) => ({
    id: d.id ?? null, 
    Nome: d.Nome ?? '',
    Sobrenome: d.Sobrenome ?? '',
    Email: d.Email ?? '',
    NumeroTelefone: d.NumeroTelefone ?? '',
    CPF: d.CPF ?? '',
    PIS: d.PIS ?? '',
    IdVaga: d.IdVaga ?? '',
    TituloVaga: d.TituloVaga ?? '',
    Escolaridade: d.Escolaridade ?? '',
    EstadoLocalizacao: d.EstadoLocalizacao ?? '',
    CidadeLocalizacao: d.CidadeLocalizacao ?? '',
    BairroLocalizacao: d.BairroLocalizacao ?? '',
    RuaLocalizacao: d.RuaLocalizacao ?? '',
    NumeroLocalizacao: d.NumeroLocalizacao ?? '',
    CEPLocalizacao: d.CEPLocalizacao ?? '',
    ComplementoLocalizacao: d.ComplementoLocalizacao ?? '',
    NumeroCNH: d.NumeroCNH ?? '',
    CategoriaCNH: d.CategoriaCNH ?? '',
    ValidadeCNH: d.ValidadeCNH ? String(d.ValidadeCNH).slice(0, 10) : '',
    ImgCurriculo: d.ImgCurriculo ?? '',
    ImgCNH: d.ImgCNH ?? '',
    Visualizado: d.Visualizado ?? '',
    experienciasProfissionais: Array.isArray(d.experienciasProfissionais) ? d.experienciasProfissionais : [],
  });

  useEffect(() => {
    if ((isReadOnly || isEditMode) && id && id !== 'criar') {
      const load = async () => {
        try {
          setLoadingData(true);
          const data = await getCandidatoById(id);
          setInitialData(mapApiToForm(data));
          statusVisualizado(id);
        } catch (err) {
          notification.error('Não foi possível carregar o candidato.');
        } finally {
          setLoadingData(false);
        }
      };
      load();
    }
  }, [id, isEditMode, isReadOnly]);

  const defaultValues = useMemo(
    () => ({
      id: null,  
      Nome: '',
      Sobrenome: '',
      Email: '',
      NumeroTelefone: '',
      CPF: '',
      PIS: '',
      IdVaga: '',
      TituloVaga: '',
      Escolaridade: '',
      EstadoLocalizacao: '',
      CidadeLocalizacao: '',
      BairroLocalizacao: '',
      RuaLocalizacao: '',
      NumeroLocalizacao: '',
      CEPLocalizacao: '',
      ComplementoLocalizacao: '',
      NumeroCNH: '',
      CategoriaCNH: '',
      ValidadeCNH: '',
      ImgCurriculo: '',
      ImgCNH: '',
      Visualizado: '',
      experienciasProfissionais: [],
    }),
    []
  );

  const initialValues = initialData || defaultValues;
  const tabs = useMemo(() => formTabs(initialValues), [initialValues]);
  const [activeTab, setActiveTab] = useState(() => (tabs.length > 0 ? tabs[0].id : null));

  useEffect(() => {
    if (!tabs.length) {
      return;
    }

    const isActiveTabValid = activeTab && tabs.some((tab) => tab.id === activeTab);
    if (!isActiveTabValid) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  const currentTab = useMemo(() => {
    if (!tabs.length) {
      return null;
    }

    const tabMatch = activeTab ? tabs.find((tab) => tab.id === activeTab) : null;
    return tabMatch || tabs[0];
  }, [tabs, activeTab]);

  const currentSections = useMemo(() => currentTab?.sections ?? [], [currentTab]);
  const isExperienciasTab = currentTab?.render === 'experiencias';
  const shouldRenderEnderecoMap = currentTab?.renderExtra === 'map';

  const onSubmit = useCallback(
    async (values) => {
      try {
        setLoading(true);

        if (isEditMode) {
          const payload = {
            Categoria: values.Categoria?.trim() || '',
            Nome: values.Nome?.trim() || '',
          };
          await updateBeneficio(id, payload);
          notification.success('Candidato atualizado com sucesso!');
        } else {
          const payload = {
            categoria: values.Categoria?.trim() || '',
            nome: values.Nome?.trim() || '',
          };
          await createBeneficio(payload);
          notification.success('Candidato criado com sucesso!');
        }

        history.goBack();
      } catch (err) {
        console.error(err);
        notification.error('Erro ao salvar o Candidato. Tente novamente.');
      } finally {
        setLoading(false);
      }
    },
    [history, id, isEditMode]
  );

  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: 50 }}>Carregando dados do Candidato...</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validateForm}
      key={id || 'criar'}
      keepDirtyOnReinitialize={false}
    >
      {({ handleSubmit, values }) => {
        const formValues = values || initialValues;
        const experienciasData = values?.experienciasProfissionais ?? initialValues.experienciasProfissionais;

        return (
          <Card>
          <CardBody>
            <CandidatoQuickActions candidato={initialValues} />

            {tabs.length > 1 && (
              <BorderedBottomTabs className="mb-4">
                <Nav variant="tabs" className="nav-tabs">
                  {tabs.map((tab) => (
                    <NavItem key={tab.id}>
                      <CustomNavLink active={tab.id === activeTab} onClick={() => setActiveTab(tab.id)}>
                        <span className="tab-text">{tab.label}</span>
                      </CustomNavLink>
                    </NavItem>
                  ))}
                </Nav>
              </BorderedBottomTabs>
            )}

            <FormContainer onSubmit={handleSubmit}>
              {isExperienciasTab ? (
                <ExperienciasProfissionais experiencias={experienciasData} />
              ) : (
                <>
                  <DynamicForm
                    structure={formStructure}
                    sections={currentSections}
                    isViewMode={isReadOnly}
                    isEditing={isEditMode}
                  />
                  {shouldRenderEnderecoMap ? (
                    <EnderecoMap endereco={formValues} />
                  ) : null}
                </>
              )}

              {!isReadOnly && (
                <FormButtonToolbar className="mt-5">
                  <ButtonWithLoading
                    variant="primary"
                    type="submit"
                    loading={loading ? 'true' : undefined}
                    disabled={loading}
                  >
                    {isEditMode ? 'Atualizar' : 'Salvar'}
                  </ButtonWithLoading>

                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => history.goBack()}
                  >
                    Cancelar
                  </Button>
                </FormButtonToolbar>
              )}
            </FormContainer>
          </CardBody>
        </Card>
        );
      }}
    </Form>
  );
};

export default Forms;
